import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Mic, Sparkles, MessageSquare, TrendingUp, TrendingDown } from 'lucide-react';
import { apiFetch } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerVoicePage() {
  const [topics, setTopics] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [topicsRes, summaryRes] = await Promise.all([
        apiFetch('/api/customer-voice/topics', { credentials: 'include' }),
        apiFetch('/api/customer-voice/ai-summary', { credentials: 'include' }),
      ]);

      if (topicsRes.ok) setTopics(await topicsRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (error) {
      console.error('Error fetching customer voice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentColor = (score) => {
    if (score >= 60) return 'text-[#2eb4ac]';
    if (score >= 40) return 'text-[#f59e0b]';
    return 'text-destructive';
  };

  const getSentimentBg = (score) => {
    if (score >= 60) return 'bg-[#2eb4ac]';
    if (score >= 40) return 'bg-[#f59e0b]';
    return 'bg-destructive';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-voice-page">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Customer Voice AI</h1>
        <p className="text-muted-foreground mt-1">
          AI-driven topic clustering and customer feedback analysis
        </p>
      </div>

      {/* AI Summary */}
      <Card className="card-hover bg-gradient-to-r from-[#2eb4ac]/5 to-transparent border-[#2eb4ac]/20" data-testid="ai-summary-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2eb4ac]" />
            AI Executive Summary
          </CardTitle>
          <CardDescription>
            Automated insights from customer feedback analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          {summary?.summary ? (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed">{summary.summary}</p>
              {summary.stats && (
                <div className="flex items-center gap-4 pt-4 border-t border-border">
                  <Badge variant="secondary" className="gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {summary.stats.total_reviews} reviews analyzed
                  </Badge>
                  <Badge className="bg-[#2eb4ac]/10 text-[#2eb4ac] gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {summary.stats.sentiments?.positive || 0} positive
                  </Badge>
                  <Badge className="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300 gap-1">
                    <TrendingDown className="w-3 h-3" />
                    {summary.stats.sentiments?.negative || 0} negative
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground">
              No analyzed reviews yet. Upload and analyze reviews to see AI summary.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Topic Clusters */}
      <div>
        <h2 className="text-xl font-semibold font-heading mb-4 flex items-center gap-2">
          <Mic className="w-5 h-5 text-[#2eb4ac]" />
          Topic Clusters
        </h2>

        {topics.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {topics.map((topic, idx) => (
              <Card
                key={topic.topic}
                className="card-hover stagger-item"
                data-testid={`topic-card-${idx}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium capitalize">{topic.topic}</h3>
                      <p className="text-sm text-muted-foreground">
                        {topic.mentions.toLocaleString()} mentions
                      </p>
                    </div>
                    <span className={`text-lg font-bold ${getSentimentColor(topic.sentiment_score)}`}>
                      {topic.sentiment_score}%
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Positive sentiment</span>
                      <span className={getSentimentColor(topic.positive_percentage)}>
                        {topic.positive_percentage}%
                      </span>
                    </div>
                    <Progress
                      value={topic.positive_percentage}
                      className="h-1.5"
                      indicatorClassName={getSentimentBg(topic.positive_percentage)}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Mic className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Topic Data</h3>
              <p className="text-muted-foreground">
                Upload and analyze reviews to see topic clusters.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
